import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Plus, Settings } from "lucide-react";
import { getErrorMessage } from "@/utils/errors";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateProject, useProjects } from "@/queries/orgs";
import { useUser } from "@/queries/user";

export function OrgPage() {
  const { orgId } = useParams({ from: "/orgs/$orgId" });
  const projects = useProjects(orgId);
  const create = useCreateProject(orgId);
  const user = useUser();
  const membership = user.data?.organizations.find((m) => m.org_id === orgId);
  const role = membership?.role;
  const orgName = membership?.name;
  const canCreate = role === "owner" || role === "admin";
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");

  return (
    <div className="space-y-6">
      <Helmet><title>{orgName ? `TodoLLM - ${orgName}` : "TodoLLM"}</title></Helmet>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-3xl font-semibold tracking-tight">Projects</h2>
          <p className="text-sm text-muted-foreground">
            Mint a project-scoped API key from <code>Settings -&gt; API keys</code> to wire an agent into one of these.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/orgs/$orgId/settings" params={{ orgId }}>
            <Settings className="size-4" />
            Organization settings
          </Link>
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Projects in this org</CardTitle>
        </CardHeader>
        <CardContent>
          {projects.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : projects.data && projects.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {projects.data.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                >
                  <Link
                    to="/orgs/$orgId/projects/$projectId"
                    params={{ orgId, projectId: p.id }}
                    className="font-medium underline-offset-4 hover:underline"
                  >
                    {p.name}{" "}
                    <span className="text-muted-foreground">/{p.slug}</span>
                  </Link>
                  <code className="truncate text-xs text-muted-foreground">{p.id}</code>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No projects yet.</p>
          )}
        </CardContent>
      </Card>

      {canCreate && (
      <Card>
        <CardHeader>
          <CardTitle>Create project</CardTitle>
          <CardDescription>Slug must be unique within the org.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim() || !slug.trim()) return;
              create.mutate(
                { name: name.trim(), slug: slug.trim() },
                {
                  onSuccess: () => {
                    setName("");
                    setSlug("");
                  },
                },
              );
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="project-name">Name</Label>
                <Input
                  id="project-name"
                  placeholder="Mobile app"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-slug">Slug</Label>
                <Input
                  id="project-slug"
                  placeholder="mobile-app"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                />
              </div>
            </div>
            <Button type="submit" disabled={create.isPending}>
              <Plus className="size-4" />
              {create.isPending ? "Creating..." : "Create project"}
            </Button>
            {create.error && (
              <p className="text-sm text-destructive">
                {getErrorMessage(create.error)}
              </p>
            )}
          </form>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
